import { useEffect, useRef } from 'react'
import { AppState, Platform } from 'react-native'

/**
 * Invoke `callback` whenever the app/tab returns to the foreground
 * (native AppState 'active' / web visibilitychange to 'visible').
 *
 * The callback is kept in a ref so consumers can pass a fresh closure every
 * render without re-subscribing the platform listener.
 */
export function useOnForeground(callback: () => void) {
  const cbRef = useRef(callback)
  cbRef.current = callback

  useEffect(() => {
    if (Platform.OS !== 'web') {
      const subscription = AppState.addEventListener('change', (nextState) => {
        if (nextState === 'active') cbRef.current()
      })
      return () => subscription.remove()
    }
    if (typeof document === 'undefined') return
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') cbRef.current()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])
}
